// A small, explicit set of geometry diagrams a practice item's content can
// carry (content.diagram) -- rendered as plain React SVG elements, never
// dangerouslySetInnerHTML, so this stays safe even though the underlying
// content is authored data rather than a compile-time constant. Shared
// between the learner-facing practice screen and the teacher-facing
// "preview what students will see" screens so both render the exact same
// picture, never two drifting implementations.

import type { PracticeDiagramSpec } from './practice-diagram-data';
import { GraphDiagramView } from './practice-graphs';

const LINE_GRAPH_WIDTH = 320;
const LINE_GRAPH_HEIGHT = 200;
const LINE_GRAPH_LEFT = 50;
const LINE_GRAPH_RIGHT = 300;
const LINE_GRAPH_TOP = 20;
const LINE_GRAPH_BOTTOM = 160;

function LineGraph({
  xLabel,
  yLabel,
  points,
}: {
  xLabel: string;
  yLabel: string;
  points: { label: string; value: number }[];
}) {
  const values = points.map((p) => p.value);
  const minValue = Math.min(...values, 0);
  const maxValue = Math.max(...values);
  const valueRange = maxValue - minValue || 1;
  const stepX = (LINE_GRAPH_RIGHT - LINE_GRAPH_LEFT) / (points.length - 1);

  function xFor(index: number) {
    return LINE_GRAPH_LEFT + index * stepX;
  }
  function yFor(value: number) {
    return (
      LINE_GRAPH_BOTTOM -
      ((value - minValue) / valueRange) * (LINE_GRAPH_BOTTOM - LINE_GRAPH_TOP)
    );
  }

  const description = `A line graph of ${yLabel} by ${xLabel}: ${points
    .map((p) => `${p.label} ${String(p.value)}`)
    .join(', ')}.`;
  const linePath = points
    .map(
      (p, index) =>
        `${index === 0 ? 'M' : 'L'} ${String(xFor(index))} ${String(yFor(p.value))}`,
    )
    .join(' ');

  return (
    <svg
      role="img"
      aria-label={description}
      viewBox={`0 0 ${String(LINE_GRAPH_WIDTH)} ${String(LINE_GRAPH_HEIGHT)}`}
      className="h-auto w-full max-w-sm"
    >
      <line
        x1={LINE_GRAPH_LEFT}
        y1={LINE_GRAPH_BOTTOM}
        x2={LINE_GRAPH_RIGHT}
        y2={LINE_GRAPH_BOTTOM}
        stroke="#2d4a3e"
        strokeWidth="2"
      />
      <line
        x1={LINE_GRAPH_LEFT}
        y1={LINE_GRAPH_TOP}
        x2={LINE_GRAPH_LEFT}
        y2={LINE_GRAPH_BOTTOM}
        stroke="#2d4a3e"
        strokeWidth="2"
      />
      <path d={linePath} fill="none" stroke="#2d4a3e" strokeWidth="2" />
      {points.map((p, index) => (
        <g key={p.label}>
          <circle cx={xFor(index)} cy={yFor(p.value)} r="3" fill="#2d4a3e" />
          <text
            x={xFor(index)}
            y={LINE_GRAPH_BOTTOM + 16}
            textAnchor="middle"
            className="fill-[#2d4a3e] text-xs font-semibold"
          >
            {p.label}
          </text>
          <text
            x={xFor(index)}
            y={yFor(p.value) - 8}
            textAnchor="middle"
            className="fill-[#2d4a3e] text-xs font-semibold"
          >
            {p.value}
          </text>
        </g>
      ))}
      <text
        x={LINE_GRAPH_LEFT}
        y={LINE_GRAPH_HEIGHT - 4}
        className="fill-[#2d4a3e] text-xs"
      >
        {xLabel}
      </text>
      <text x={4} y={LINE_GRAPH_TOP + 8} className="fill-[#2d4a3e] text-xs">
        {yLabel}
      </text>
    </svg>
  );
}

const BAR_CHART_WIDTH = 320;
const BAR_LEFT = 90;
const BAR_RIGHT = 300;
const BAR_HEIGHT = 28;
const BAR_GAP = 14;
const BAR_TOP = 10;

function BarChart({
  bars,
}: {
  bars: { label: string; value: number; unit: string }[];
}) {
  const maxValue = Math.max(...bars.map((b) => b.value), 1);
  const chartHeight = BAR_TOP + bars.length * (BAR_HEIGHT + BAR_GAP);
  const description = `A bar comparison: ${bars
    .map((b) => `${b.label} ${String(b.value)} ${b.unit}`)
    .join(', ')}.`;

  return (
    <svg
      role="img"
      aria-label={description}
      viewBox={`0 0 ${String(BAR_CHART_WIDTH)} ${String(chartHeight)}`}
      className="h-auto w-full max-w-sm"
    >
      {bars.map((bar, index) => {
        const y = BAR_TOP + index * (BAR_HEIGHT + BAR_GAP);
        const barWidth = (bar.value / maxValue) * (BAR_RIGHT - BAR_LEFT);
        return (
          <g key={bar.label}>
            <text
              x={BAR_LEFT - 8}
              y={y + BAR_HEIGHT / 2 + 4}
              textAnchor="end"
              className="fill-[#2d4a3e] text-xs font-semibold"
            >
              {bar.label}
            </text>
            <rect
              x={BAR_LEFT}
              y={y}
              width={Math.max(barWidth, 1)}
              height={BAR_HEIGHT}
              fill="#2d4a3e"
            />
            <text
              x={BAR_LEFT + Math.max(barWidth, 1) + 6}
              y={y + BAR_HEIGHT / 2 + 4}
              className="fill-[#2d4a3e] text-xs font-semibold"
            >
              {bar.value} {bar.unit}
            </text>
          </g>
        );
      })}
    </svg>
  );
}

export function PracticeDiagramView({
  diagram,
}: {
  diagram: PracticeDiagramSpec;
}) {
  if (diagram.type === 'bars') {
    return <BarChart bars={diagram.bars} />;
  }

  if (diagram.type === 'line_graph') {
    return (
      <LineGraph
        xLabel={diagram.xLabel}
        yLabel={diagram.yLabel}
        points={diagram.points}
      />
    );
  }

  if (diagram.type === 'rectangle') {
    const description = `A rectangle, labelled ${diagram.widthLabel} wide and ${diagram.heightLabel} tall.`;
    return (
      <svg
        role="img"
        aria-label={description}
        viewBox="0 0 220 160"
        className="h-auto w-full max-w-xs"
      >
        <rect
          x="30"
          y="25"
          width="160"
          height="100"
          fill="none"
          stroke="#2d4a3e"
          strokeWidth="3"
        />
        <text
          x="110"
          y="16"
          textAnchor="middle"
          className="fill-[#2d4a3e] text-sm font-semibold"
        >
          {diagram.widthLabel}
        </text>
        <text
          x="14"
          y="75"
          textAnchor="middle"
          transform="rotate(-90 14 75)"
          className="fill-[#2d4a3e] text-sm font-semibold"
        >
          {diagram.heightLabel}
        </text>
      </svg>
    );
  }

  if (diagram.type !== 'box') {
    return <GraphDiagramView graph={diagram} />;
  }

  const description = `A rectangular box, labelled ${diagram.lengthLabel} long, ${diagram.widthLabel} wide and ${diagram.heightLabel} high.`;
  return (
    <svg
      role="img"
      aria-label={description}
      viewBox="0 0 240 180"
      className="h-auto w-full max-w-xs"
    >
      {/* front face */}
      <rect
        x="40"
        y="60"
        width="120"
        height="80"
        fill="none"
        stroke="#2d4a3e"
        strokeWidth="3"
      />
      {/* top face */}
      <polygon
        points="40,60 80,20 200,20 160,60"
        fill="none"
        stroke="#2d4a3e"
        strokeWidth="3"
      />
      {/* side face */}
      <polygon
        points="160,60 200,20 200,100 160,140"
        fill="none"
        stroke="#2d4a3e"
        strokeWidth="3"
      />
      <text
        x="100"
        y="158"
        textAnchor="middle"
        className="fill-[#2d4a3e] text-sm font-semibold"
      >
        {diagram.lengthLabel}
      </text>
      <text
        x="18"
        y="102"
        textAnchor="middle"
        transform="rotate(-90 18 102)"
        className="fill-[#2d4a3e] text-sm font-semibold"
      >
        {diagram.widthLabel}
      </text>
      <text
        x="192"
        y="16"
        textAnchor="middle"
        className="fill-[#2d4a3e] text-sm font-semibold"
      >
        {diagram.heightLabel}
      </text>
    </svg>
  );
}
